import PropTypes from 'prop-types';
import { useRecoilState, useRecoilValue } from 'recoil';

import { chatPostOnDemandAtom } from '@/state/atom/translationAtom';
import { trI18n } from '@/common/utils/trI18n';
import TranslationConstant from '@/common/constant/TranslationConstant';
import {
    setMessageActiveView,
    getChatPostMessageRecord,
    getChatPostThreadRecord,
    isThreadToggleOn,
} from '@/component/multiLanguage/handlers/ChatPostOnDemandHandler';
import { getContentChatPost, getContentChatPostEmail } from '@/component/chatThread/handler/MessageHandler';

import './ChatPostTranslationView.css';
import CorporationUtils from '@/common/utils/CorporationUtils';
import Constant from '@/common/constant/Constant';
import { corporationAtom } from '@/state/atom/corporationAtom';

const { ON_DEMAND_TRANSLATION_VIEW } = TranslationConstant;

ChatPostTranslationView.propTypes = {
    threadId: PropTypes.string,
    postId: PropTypes.string,
    mention: PropTypes.array,
    userChatList: PropTypes.array,
    isRichText: PropTypes.bool,
    isAlignLeft: PropTypes.bool,
    children: PropTypes.node
};

/**
 * Wraps a chat message body to support on-demand translation display (CHT-005):
 * shows either the original (children) or the translated content, plus a small
 * text link to toggle between them (原文を表示 / 翻訳を表示).
 *
 * The link only appears for messages that are eligible and already have a
 * translation (produced by the whole-thread toggle). Clicking it only switches
 * this one message between its original and cached translation view (§4.2) — it
 * never triggers a translation request (dịch chỉ khởi nguồn từ toggle cả thread,
 * spec BR-1). Otherwise the original content is rendered untouched.
 */
export default function ChatPostTranslationView({
    threadId,
    selectThread,
    postId,
    mention,
    userChatList,
    isRichText,
    isAlignLeft,
    children
}) {
    const corporationDetail = useRecoilValue(corporationAtom);
    const [chatPostOnDemand, setChatPostOnDemand] = useRecoilState(chatPostOnDemandAtom);
    const message = getChatPostMessageRecord(chatPostOnDemand, threadId, postId);
    const threadRecord = getChatPostThreadRecord(chatPostOnDemand, threadId);

    if (
        !message ||
        !message.isTranslatable ||
        !CorporationUtils.isEnableSettingMultiLanguage(corporationDetail) ||
        message.translated == null ||
        !isThreadToggleOn(threadRecord)
    ) {
        return children;
    }

    // case overlap:
    if (selectThread?.id !== threadId) {
        if (selectThread?.threadType === Constant.THREAD_TYPE.AI_ASSISTANT) return children;

        const selectedThreadRecord = getChatPostThreadRecord(chatPostOnDemand, selectThread?.id);
        if (selectedThreadRecord && !isThreadToggleOn(selectedThreadRecord)) return children;
    }

    const isShowingTranslation = message.activeView === ON_DEMAND_TRANSLATION_VIEW.TRANSLATION;

    const buildTranslatedHtml = () => {
        const translated = message.translated || message.original || '';

        const translatedPost = { content: translated, mention };
        return isRichText
            ? getContentChatPostEmail(translatedPost, userChatList, isAlignLeft, true)
            : getContentChatPost(translatedPost, userChatList, isAlignLeft, true);
    };

    // Switch only this message between its original and cached translation view.
    const handleToggle = () => {
        setMessageActiveView(
            setChatPostOnDemand,
            threadId,
            postId,
            isShowingTranslation
                ? ON_DEMAND_TRANSLATION_VIEW.ORIGINAL
                : ON_DEMAND_TRANSLATION_VIEW.TRANSLATION
        );
    };

    return (
        <>
            {isShowingTranslation ? (
                <div
                    className="displayMessage chat-post-translated"
                    dangerouslySetInnerHTML={{ __html: buildTranslatedHtml() }}
                    data-cy="chat-post-translated-content"
                ></div>
            ) : (
                children
            )}

            <span
                className="chat-post-translation-toggle"
                role="button"
                tabIndex={0}
                onClick={handleToggle}
                data-cy="chat-post-translation-toggle"
            >
                {isShowingTranslation
                    ? trI18n('CM-ONDEMAND-VIEW-ORIGINAL')
                    : trI18n('CM-ONDEMAND-VIEW-TRANSLATION')}
            </span>
        </>
    );
}
